<script>
	import { onMount } from 'svelte';
	import { complementGroups, complementsLoading, syncGroups, createGroup, updateGroup, toggleGroup, removeGroup, addItem, updateItem, toggleItem, removeItem } from '../stores/complementsStore.js';
	import Icon from '../components/icons/Icon.svelte';
	import NicheOnboardingWizard from './NicheOnboardingWizard.svelte';

	export let onNotify = (msg, type) => {};

	// Estado do componente
	let isLoaded = false;
	let showNicheImporterModal = false;

	onMount(async () => {
		try {
			await syncGroups();
		} catch (err) {
			onNotify('Erro ao carregar complementos: ' + err.message, 'error');
		}
		isLoaded = true;
	});

	// ==============================
	// MODAL DE GRUPO
	// ==============================
	let showGroupModal = false;
	let editingGroup = null;
	let groupForm = { name: '', required: false, min_choice: 0, max_choice: 1, free_choices: 0 };

	function openNewGroupModal() {
		editingGroup = null;
		groupForm = { name: '', required: false, min_choice: 0, max_choice: 1, free_choices: 0 };
		showGroupModal = true;
	}

	function openEditGroupModal(group) {
		editingGroup = group;
		groupForm = {
			name: group.name,
			required: group.required === 1,
			min_choice: group.min_choice || 0,
			max_choice: group.max_choice || 1,
			free_choices: group.free_choices || 0
		};
		showGroupModal = true;
	}

	async function handleSaveGroup() {
		if (!groupForm.name.trim()) {
			onNotify('O nome do grupo é obrigatório.', 'error');
			return;
		}
		try {
			if (editingGroup) {
				await updateGroup(editingGroup.id, groupForm);
				onNotify('Grupo atualizado com sucesso!', 'success');
			} else {
				await createGroup(groupForm);
				onNotify('Grupo criado com sucesso!', 'success');
			}
			showGroupModal = false;
		} catch (err) {
			onNotify(err.message, 'error');
		}
	}

	async function handleToggleGroup(group) {
		try {
			await toggleGroup(group.id);
			const status = group.active === 1 ? 'desativado' : 'reativado';
			onNotify(`Grupo "${group.name}" ${status}.`, 'success');
		} catch (err) {
			onNotify(err.message, 'error');
		}
	}

	async function handleDeleteGroup(group) {
		if (!confirm(`Tem certeza que deseja excluir o grupo "${group.name}" e todos os seus itens?`)) return;
		try {
			await removeGroup(group.id);
			onNotify('Grupo excluído com sucesso.', 'success');
			if (expandedGroupId === group.id) expandedGroupId = null;
		} catch (err) {
			onNotify(err.message, 'error');
		}
	}

	// ==============================
	// ITENS DO GRUPO
	// ==============================
	let expandedGroupId = null;
	let showItemForm = null; // groupId de onde está aberto
	let editingItem = null;
	let itemForm = { name: '', price: 0 };

	function toggleExpandGroup(groupId) {
		expandedGroupId = expandedGroupId === groupId ? null : groupId;
		showItemForm = null;
		editingItem = null;
	}

	function openNewItemForm(groupId) {
		showItemForm = groupId;
		editingItem = null;
		itemForm = { name: '', price: 0 };
	}

	function openEditItemForm(item) {
		showItemForm = item.group_id;
		editingItem = item;
		itemForm = { name: item.name, price: Number(item.price) || 0 };
	}

	function cancelItemForm() {
		showItemForm = null;
		editingItem = null;
	}

	async function handleSaveItem(groupId) {
		if (!itemForm.name.trim()) {
			onNotify('O nome do item é obrigatório.', 'error');
			return;
		}
		try {
			if (editingItem) {
				await updateItem(editingItem.id, itemForm);
				onNotify('Item atualizado!', 'success');
			} else {
				await addItem(groupId, itemForm);
				onNotify('Item adicionado!', 'success');
			}
			showItemForm = null;
			editingItem = null;
		} catch (err) {
			onNotify(err.message, 'error');
		}
	}

	async function handleToggleItem(item) {
		try {
			await toggleItem(item.id);
		} catch (err) {
			onNotify(err.message, 'error');
		}
	}

	async function handleDeleteItem(item) {
		if (!confirm(`Excluir "${item.name}"?`)) return;
		try {
			await removeItem(item.id);
			onNotify('Item excluído.', 'success');
		} catch (err) {
			onNotify(err.message, 'error');
		}
	}

	// Formatação
	function formatPrice(price) {
		return Number(price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
	}
</script>

<div class="complements-manager">
	{#if !isLoaded}
		<div class="loading-state">
			<div class="spinner"></div>
			<span>Carregando complementos...</span>
		</div>
	{:else if $complementGroups.length === 0}
		<!-- EMPTY STATE -->
		<div class="empty-state">
			<div class="empty-icon">
				<Icon name="layers" size={48} color="#94A3B8" />
			</div>
			<h3>Nenhum grupo de complementos</h3>
			<p>Crie grupos reutilizáveis de complementos que podem ser associados a vários produtos.</p>
			<div class="empty-state-actions">
				<button type="button" class="btn-niche-templates" on:click={() => showNicheImporterModal = true}>
					<span>⚡ Carregar Modelos por Nicho</span>
				</button>
				<button type="button" class="btn-create-first" on:click={openNewGroupModal}>
					<Icon name="plus" size={16} />
					<span>Criar grupo manualmente</span>
				</button>
			</div>
		</div>
	{:else}
		<!-- HEADER DE AÇÕES -->
		<div class="complements-toolbar">
			<div class="toolbar-info">
				<span class="toolbar-count">{$complementGroups.length} {$complementGroups.length === 1 ? 'grupo' : 'grupos'}</span>
			</div>
			<div class="toolbar-actions">
				<button type="button" class="btn-niche-templates-toolbar" on:click={() => showNicheImporterModal = true}>
					<span>⚡ Modelos por Nicho</span>
				</button>
				<button type="button" class="btn-add-group" on:click={openNewGroupModal}>
					<Icon name="plus" size={14} />
					<span>Novo Grupo</span>
				</button>
			</div>
		</div>

		<!-- LISTA DE GRUPOS -->
		<div class="groups-list">
			{#each $complementGroups as group (group.id)}
				<div class="group-card {group.active !== 1 ? 'inactive' : ''}">
					<!-- HEADER DO GRUPO -->
					<div class="group-header" on:click={() => toggleExpandGroup(group.id)}>
						<div class="group-info">
							<div class="group-name-row">
								<span class="group-name">{group.name}</span>
								{#if group.required === 1}
									<span class="badge-required">Obrigatório</span>
								{:else}
									<span class="badge-optional">Opcional</span>
								{/if}
								{#if group.active !== 1}
									<span class="badge-inactive">Inativo</span>
								{/if}
							</div>
							<div class="group-meta">
								<span>{(group.items || []).length} {(group.items || []).length === 1 ? 'item' : 'itens'}</span>
								<span class="meta-sep">·</span>
								<span>Máx {group.max_choice} {group.max_choice === 1 ? 'escolha' : 'escolhas'}</span>
								{#if group.free_choices > 0}
									<span class="meta-sep">·</span>
									<span>{group.free_choices} grátis</span>
								{/if}
								{#if group.product_count > 0}
									<span class="meta-sep">·</span>
									<span class="meta-linked">{group.product_count} {group.product_count === 1 ? 'produto' : 'produtos'}</span>
								{/if}
							</div>
						</div>
						<div class="group-actions">
							<button type="button" class="btn-icon-sm" title="Editar grupo" on:click|stopPropagation={() => openEditGroupModal(group)}>
								<Icon name="edit" size={14} color="#64748B" />
							</button>
							<button type="button" class="btn-icon-sm" title="{group.active === 1 ? 'Desativar' : 'Ativar'}" on:click|stopPropagation={() => handleToggleGroup(group)}>
								<Icon name={group.active === 1 ? 'eye' : 'eye-off'} size={14} color={group.active === 1 ? '#10B981' : '#EF4444'} />
							</button>
							<button type="button" class="btn-icon-sm danger" title="Excluir grupo" on:click|stopPropagation={() => handleDeleteGroup(group)}>
								<Icon name="trash" size={14} color="#EF4444" />
							</button>
							<div class="expand-chevron {expandedGroupId === group.id ? 'open' : ''}">
								<Icon name="chevron-down" size={16} color="#94A3B8" />
							</div>
						</div>
					</div>

					<!-- ITENS (EXPANDIDO) -->
					{#if expandedGroupId === group.id}
						<div class="group-items-panel">
							{#if (group.items || []).length === 0}
								<div class="items-empty">
									<span>Nenhum item neste grupo</span>
								</div>
							{:else}
								<div class="items-list">
									{#each (group.items || []) as item (item.id)}
										<div class="item-row {item.active !== 1 ? 'inactive' : ''}">
											<div class="item-info">
												<span class="item-name">{item.name}</span>
												<span class="item-price">{formatPrice(item.price)}</span>
											</div>
											<div class="item-actions">
												<button type="button" class="btn-icon-xs" on:click={() => openEditItemForm(item)} title="Editar">
													<Icon name="edit" size={12} color="#64748B" />
												</button>
												<button type="button" class="btn-icon-xs" on:click={() => handleToggleItem(item)} title="{item.active === 1 ? 'Desativar' : 'Ativar'}">
													<Icon name={item.active === 1 ? 'eye' : 'eye-off'} size={12} color={item.active === 1 ? '#10B981' : '#94A3B8'} />
												</button>
												<button type="button" class="btn-icon-xs danger" on:click={() => handleDeleteItem(item)} title="Excluir">
													<Icon name="trash" size={12} color="#EF4444" />
												</button>
											</div>
										</div>
									{/each}
								</div>
							{/if}

							<!-- FORM DE ITEM INLINE -->
							{#if showItemForm === group.id}
								<div class="item-form-inline">
									<input type="text" class="form-input-sm" placeholder="Nome do item (ex: Bacon)" bind:value={itemForm.name} />
									<input type="number" step="0.01" min="0" class="form-input-sm price-input" placeholder="0.00" bind:value={itemForm.price} />
									<button type="button" class="btn-save-item" on:click={() => handleSaveItem(group.id)}>
										{editingItem ? 'Salvar' : 'Adicionar'}
									</button>
									<button type="button" class="btn-cancel-item" on:click={cancelItemForm}>✕</button>
								</div>
							{:else}
								<button type="button" class="btn-add-item" on:click={() => openNewItemForm(group.id)}>
									<Icon name="plus" size={12} />
									<span>Adicionar item</span>
								</button>
							{/if}
						</div>
					{/if}
				</div>
			{/each}
		</div>
	{/if}
</div>

<!-- MODAL DE GRUPO -->
{#if showGroupModal}
	<div class="modal-overlay" on:click|self={() => showGroupModal = false}>
		<div class="modal-box">
			<div class="modal-header">
				<h3>{editingGroup ? 'Editar Grupo' : 'Novo Grupo de Complementos'}</h3>
				<button type="button" class="modal-close" on:click={() => showGroupModal = false}>✕</button>
			</div>
			<div class="modal-body">
				<div class="form-row">
					<label class="field-label">Nome do Grupo *</label>
					<input type="text" class="form-input" placeholder="Ex: Turbine seu Burger, Molhos, Bordas..." bind:value={groupForm.name} />
				</div>
				<div class="form-grid-2">
					<div class="form-row">
						<label class="field-label">Tipo</label>
						<select class="form-input" bind:value={groupForm.required}>
							<option value={false}>Opcional</option>
							<option value={true}>Obrigatório</option>
						</select>
					</div>
					<div class="form-row">
						<label class="field-label">Máximo de escolhas</label>
						<input type="number" min="1" max="20" class="form-input" bind:value={groupForm.max_choice} />
					</div>
				</div>
				<div class="form-grid-2">
					<div class="form-row">
						<label class="field-label">Mínimo de escolhas</label>
						<input type="number" min="0" max="20" class="form-input" bind:value={groupForm.min_choice} />
					</div>
					<div class="form-row">
						<label class="field-label">Escolhas grátis</label>
						<input type="number" min="0" max="20" class="form-input" bind:value={groupForm.free_choices} />
					</div>
				</div>
			</div>
			<div class="modal-footer">
				<button type="button" class="btn-modal-cancel" on:click={() => showGroupModal = false}>Cancelar</button>
				<button type="button" class="btn-modal-save" on:click={handleSaveGroup}>
					{editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
				</button>
			</div>
		</div>
	</div>
{/if}

<style>
	.complements-manager {
		padding: 0;
		display: flex;
		flex-direction: column;
		width: 100%;
		background: #F8FAFC;
	}

	/* Loading */
	.loading-state {
		display: flex; align-items: center; justify-content: center; gap: 12px;
		padding: 80px 20px; color: #94A3B8; font-size: 14px;
	}
	.spinner {
		width: 24px; height: 24px; border: 3px solid #E2E8F0;
		border-top-color: #FF6A00; border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	/* Empty State */
	.empty-state {
		text-align: center; padding: 80px 40px;
	}
	.empty-icon { margin-bottom: 20px; opacity: 0.5; }
	.empty-state h3 {
		font-size: 18px; font-weight: 600; color: #334155; margin: 0 0 8px;
	}
	.empty-state p {
		font-size: 14px; color: #64748B; margin: 0 0 6px; max-width: 440px; margin-left: auto; margin-right: auto;
	}
	.example-text {
		font-size: 13px !important; color: #94A3B8 !important; font-style: italic;
		margin-bottom: 24px !important;
	}
	.btn-create-first {
		display: inline-flex; align-items: center; gap: 8px;
		padding: 10px 24px; background: #FF6A00; color: #fff;
		border: none; border-radius: 10px; font-size: 14px; font-weight: 600;
		cursor: pointer; transition: all 0.2s;
	}
	.btn-create-first:hover { background: #E85A00; transform: translateY(-1px); }

	/* Toolbar */
	.complements-toolbar {
		position: sticky;
		top: 0;
		z-index: 20;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 24px;
		border-bottom: 1px solid #E2E8F0;
		background: #FFFFFF;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
		flex-shrink: 0;
	}
	.toolbar-count { font-size: 14px; color: #64748B; font-weight: 500; }
	.btn-add-group {
		display: inline-flex; align-items: center; gap: 6px;
		padding: 8px 16px; background: #FF6A00; color: #fff;
		border: none; border-radius: 8px; font-size: 13px; font-weight: 600;
		cursor: pointer; transition: all 0.2s;
	}
	.btn-add-group:hover { background: #E85A00; }

	/* Groups List */
	.groups-list {
		padding: 16px 24px 100px 24px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
		box-sizing: border-box;
	}

	.group-card {
		background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 12px;
		overflow: hidden; transition: all 0.2s;
	}
	.group-card:hover { border-color: #CBD5E1; }
	.group-card.inactive { opacity: 0.6; }

	.group-header {
		display: flex; align-items: center; justify-content: space-between;
		padding: 14px 16px; cursor: pointer; user-select: none;
	}
	.group-header:hover { background: #F8FAFC; }

	.group-info { flex: 1; min-width: 0; }
	.group-name-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
	.group-name { font-size: 15px; font-weight: 600; color: #0F172A; }

	.badge-required {
		font-size: 10px; font-weight: 700; padding: 2px 8px;
		background: #FEF3C7; color: #92400E; border-radius: 20px; text-transform: uppercase;
	}
	.badge-optional {
		font-size: 10px; font-weight: 700; padding: 2px 8px;
		background: #F0FDF4; color: #166534; border-radius: 20px; text-transform: uppercase;
	}
	.badge-inactive {
		font-size: 10px; font-weight: 700; padding: 2px 8px;
		background: #FEE2E2; color: #991B1B; border-radius: 20px; text-transform: uppercase;
	}

	.group-meta {
		display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
		margin-top: 4px; font-size: 12px; color: #94A3B8;
	}
	.meta-sep { color: #CBD5E1; }
	.meta-linked { color: #3B82F6; font-weight: 500; }

	.group-actions { display: flex; align-items: center; gap: 4px; margin-left: 12px; }
	.btn-icon-sm {
		width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
		background: transparent; border: 1px solid transparent; border-radius: 8px; cursor: pointer;
		transition: all 0.15s;
	}
	.btn-icon-sm:hover { background: #F1F5F9; border-color: #E2E8F0; }
	.btn-icon-sm.danger:hover { background: #FEF2F2; }

	.expand-chevron { transition: transform 0.2s; }
	.expand-chevron.open { transform: rotate(180deg); }

	/* Items Panel */
	.group-items-panel {
		border-top: 1px solid #F1F5F9; padding: 8px 16px 12px;
		background: #F8FAFC;
	}
	.items-empty {
		text-align: center; padding: 16px; color: #94A3B8; font-size: 13px;
	}
	.items-list {
		display: flex;
		flex-direction: column;
		gap: 4px;
		max-height: 340px;
		overflow-y: auto;
		padding-right: 6px;
		scrollbar-width: thin;
		scrollbar-color: #CBD5E1 transparent;
	}

	.items-list::-webkit-scrollbar {
		width: 6px;
	}

	.items-list::-webkit-scrollbar-track {
		background: #F1F5F9;
		border-radius: 6px;
	}

	.items-list::-webkit-scrollbar-thumb {
		background: #CBD5E1;
		border-radius: 6px;
	}

	.items-list::-webkit-scrollbar-thumb:hover {
		background: #94A3B8;
	}

	.item-row {
		display: flex; align-items: center; justify-content: space-between;
		padding: 8px 12px; background: #FFFFFF; border: 1px solid #E2E8F0;
		border-radius: 8px; transition: all 0.15s;
	}
	.item-row:hover { border-color: #CBD5E1; }
	.item-row.inactive { opacity: 0.5; }

	.item-info { display: flex; align-items: center; gap: 12px; flex: 1; }
	.item-name { font-size: 14px; color: #334155; font-weight: 500; }
	.item-price { font-size: 13px; color: #10B981; font-weight: 600; }

	.item-actions { display: flex; align-items: center; gap: 2px; }
	.btn-icon-xs {
		width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
		background: transparent; border: none; border-radius: 6px; cursor: pointer;
		transition: all 0.15s;
	}
	.btn-icon-xs:hover { background: #F1F5F9; }
	.btn-icon-xs.danger:hover { background: #FEF2F2; }

	/* Item Form Inline */
	.item-form-inline {
		display: flex; align-items: center; gap: 8px;
		margin-top: 8px; padding: 8px 0;
	}
	.form-input-sm {
		flex: 1; padding: 7px 10px; border: 1px solid #E2E8F0;
		border-radius: 8px; font-size: 13px; color: #334155;
		outline: none; transition: border-color 0.2s;
	}
	.form-input-sm:focus { border-color: #FF6A00; }
	.price-input { max-width: 100px; flex: 0; }

	.btn-save-item {
		padding: 7px 14px; background: #FF6A00; color: #fff;
		border: none; border-radius: 8px; font-size: 12px; font-weight: 600;
		cursor: pointer; white-space: nowrap;
	}
	.btn-save-item:hover { background: #E85A00; }
	.btn-cancel-item {
		width: 28px; height: 28px; display: flex; align-items: center; justify-content: center;
		background: transparent; border: 1px solid #E2E8F0; border-radius: 6px;
		color: #94A3B8; cursor: pointer; font-size: 14px;
	}

	.btn-add-item {
		display: inline-flex; align-items: center; gap: 6px;
		margin-top: 8px; padding: 6px 12px; background: transparent;
		border: 1px dashed #CBD5E1; border-radius: 8px; color: #64748B;
		font-size: 12px; cursor: pointer; transition: all 0.2s;
	}
	.btn-add-item:hover { border-color: #FF6A00; color: #FF6A00; background: #FFF7ED; }

	/* Modal */
	.modal-overlay {
		position: fixed; inset: 0; background: rgba(0,0,0,0.5);
		display: flex; align-items: center; justify-content: center;
		z-index: 1000; backdrop-filter: blur(2px);
	}
	.modal-box {
		background: #fff; border-radius: 16px; width: 480px; max-width: 90vw;
		max-height: 90vh; display: flex; flex-direction: column;
		box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
	}
	.modal-header {
		display: flex; align-items: center; justify-content: space-between;
		padding: 20px 24px; border-bottom: 1px solid #F1F5F9;
		flex-shrink: 0;
	}
	.modal-header h3 { font-size: 16px; font-weight: 700; color: #0F172A; margin: 0; }
	.modal-close {
		width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
		background: #F1F5F9; border: none; border-radius: 8px;
		color: #64748B; cursor: pointer; font-size: 16px;
	}
	.modal-close:hover { background: #E2E8F0; }

	.modal-body { padding: 20px 24px; overflow-y: auto; flex: 1; }
	.form-row { margin-bottom: 14px; }
	.field-label {
		display: block; font-size: 13px; font-weight: 600;
		color: #475569; margin-bottom: 6px;
	}
	.form-input {
		width: 100%; padding: 9px 12px; border: 1px solid #E2E8F0;
		border-radius: 10px; font-size: 14px; color: #334155;
		outline: none; transition: border-color 0.2s; box-sizing: border-box;
	}
	.form-input:focus { border-color: #FF6A00; }
	.form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

	.modal-footer {
		display: flex; align-items: center; justify-content: flex-end; gap: 10px;
		padding: 16px 24px; border-top: 1px solid #F1F5F9;
	}
	.btn-modal-cancel {
		padding: 9px 20px; background: #F1F5F9; color: #475569;
		border: none; border-radius: 10px; font-size: 14px; font-weight: 500;
		cursor: pointer;
	}
	.btn-modal-cancel:hover { background: #E2E8F0; }
	.btn-modal-save {
		padding: 9px 24px; background: #FF6A00; color: #fff;
		border: none; border-radius: 10px; font-size: 14px; font-weight: 600;
		cursor: pointer;
	}
	.btn-modal-save:hover { background: #E85A00; }

	/* Estilos de Integração de Nichos */
	.empty-state-actions {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		margin-top: 8px;
	}

	.btn-niche-templates {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		background: #FFF7ED;
		color: #EA580C;
		border: 1.5px solid #FDBA74;
		border-radius: 10px;
		padding: 10px 22px;
		font-size: 14px;
		font-weight: 700;
		cursor: pointer;
		box-shadow: 0 2px 8px rgba(234, 88, 12, 0.1);
		transition: all 0.15s;
	}

	.btn-niche-templates:hover {
		background: #EA580C;
		color: #FFFFFF;
		transform: translateY(-1px);
		box-shadow: 0 4px 14px rgba(234, 88, 12, 0.25);
	}

	.toolbar-actions {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.btn-niche-templates-toolbar {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: #FFF7ED;
		color: #EA580C;
		border: 1px solid #FDBA74;
		border-radius: 8px;
		padding: 7px 14px;
		font-size: 12.5px;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s;
	}

	.btn-niche-templates-toolbar:hover {
		background: #FFEDD5;
		border-color: #EA580C;
	}

	.modal-container-niche {
		width: 100%;
		max-width: 960px;
		max-height: 92vh;
		background: #0E121A;
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 20px;
		overflow: hidden;
		box-shadow: 0 25px 70px -15px rgba(0, 0, 0, 0.9), 0 0 40px rgba(255, 106, 0, 0.12);
		display: flex;
		flex-direction: column;
		color: #FFFFFF;
	}
</style>

{#if showNicheImporterModal}
	<div class="modal-overlay" on:click|self={() => showNicheImporterModal = false}>
		<div class="modal-container-niche">
			<NicheOnboardingWizard
				isStandaloneImporter={true}
				onNotify={(msg, type) => onNotify(msg, type)}
				onComplete={() => {
					showNicheImporterModal = false;
				}}
			/>
		</div>
	</div>
{/if}
